/**
 * CodeViewer Component
 *
 * Displays code files with syntax highlighting.
 * Supports JavaScript, HTML, CSS, Python, and other languages.
 * Shows line numbers and allows code copying.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, Tooltip,
  Alert, TextField, InputAdornment, Collapse, Divider, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  ContentCopy, Brightness4, Brightness7, Search, Clear,
  Settings, WrapText, Fullscreen, FullscreenExit, Tag
} from '@mui/icons-material';
import { CodeSearchContext } from '../../contexts/CodeSearchContext';

interface CodeViewerProps {
  code: string;
  language?: string;
  filename?: string;
  showLineNumbers?: boolean;
  questionNumber?: number;
}

const FONT_SIZES: Record<string, string> = { S: '0.72rem', M: '0.85rem', L: '1rem' };

const CodeViewer: React.FC<CodeViewerProps> = ({
  code,
  language = 'javascript',
  filename,
  showLineNumbers: showLineNumbersProp = true,
  questionNumber
}) => {
  const [darkMode, setDarkMode] = useState(true);
  const [copied, setCopied] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState<string | null>(null);
  const [localCaseSensitive, setLocalCaseSensitive] = useState(false);

  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [wrapCode, setWrapCode] = useState(false);
  const [fontSize, setFontSize] = useState<'S' | 'M' | 'L'>('M');
  const [lineNumbers, setLineNumbers] = useState(showLineNumbersProp);
  const [fullScreen, setFullScreen] = useState(false);

  const codeContainerRef = useRef<HTMLDivElement>(null);

  const context = React.useContext(CodeSearchContext);
  const shouldReceiveContextSearch = !context?.questionFilter || context.questionFilter === questionNumber;

  useEffect(() => {
    setLocalSearchTerm(null);
  }, [context?.searchTerm, context?.questionFilter]);

  const searchTerm = localSearchTerm !== null
    ? localSearchTerm
    : (shouldReceiveContextSearch && context?.searchTerm ? context.searchTerm : '');
  const caseSensitive = localCaseSensitive;
  const setCaseSensitive = setLocalCaseSensitive;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const matchingLines = useMemo(() => {
    if (!searchTerm) return new Set<number>();
    const searchRegex = new RegExp(
      searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      caseSensitive ? 'g' : 'gi'
    );
    const lines = code.split('\n');
    const matches = new Set<number>();
    lines.forEach((line, index) => {
      if (searchRegex.test(line)) matches.add(index + 1);
    });
    return matches;
  }, [code, searchTerm, caseSensitive]);

  useEffect(() => {
    const isLocalSearch = !!localSearchTerm;
    const isExplicitlyTargeted =
      context?.questionFilter != null && context.questionFilter === questionNumber;

    if (searchTerm && matchingLines.size > 0 && codeContainerRef.current && (isLocalSearch || isExplicitlyTargeted)) {
      const scrollTimer = setTimeout(() => {
        const highlightedElement = codeContainerRef.current?.querySelector('.highlighted-line');
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } else {
          codeContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      return () => clearTimeout(scrollTimer);
    }
  }, [searchTerm, matchingLines, localSearchTerm, context?.questionFilter, questionNumber]);

  const matchCount = useMemo(() => {
    if (!searchTerm) return 0;
    const searchRegex = new RegExp(
      searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      caseSensitive ? 'g' : 'gi'
    );
    return (code.match(searchRegex) || []).length;
  }, [code, searchTerm, caseSensitive]);

  const getLanguageDisplayName = (lang: string): string => {
    const langMap: { [key: string]: string } = {
      javascript: 'JavaScript', js: 'JavaScript',
      typescript: 'TypeScript', ts: 'TypeScript',
      html: 'HTML', htm: 'HTML',
      css: 'CSS', python: 'Python', py: 'Python',
      java: 'Java', cpp: 'C++', c: 'C', csharp: 'C#',
      go: 'Go', rust: 'Rust', sql: 'SQL', json: 'JSON',
      yaml: 'YAML', xml: 'XML', markdown: 'Markdown', md: 'Markdown'
    };
    return langMap[lang.toLowerCase()] || lang.toUpperCase();
  };

  const getLanguageColor = (lang: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const l = lang.toLowerCase();
    if (l.includes('javascript') || l === 'js') return 'warning';
    if (l.includes('html') || l === 'htm') return 'error';
    if (l.includes('css')) return 'info';
    if (l.includes('python') || l === 'py') return 'success';
    if (l.includes('typescript') || l === 'ts') return 'primary';
    return 'default';
  };

  if (!code || code.trim() === '') {
    return <Alert severity="info">No code content available to display</Alert>;
  }

  const currentFontSize = FONT_SIZES[fontSize];
  const whiteSpaceValue = wrapCode ? 'pre-wrap' : 'pre';
  const wordBreakValue = wrapCode ? 'break-word' : 'normal';

  const outerBoxSx = fullScreen
    ? { position: 'fixed' as const, top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1300, bgcolor: 'background.default', display: 'flex', flexDirection: 'column' as const, p: 2 }
    : { position: 'relative' as const, display: 'flex', flexDirection: 'column' as const };

  return (
    <Box sx={outerBoxSx}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        sx={{ position: 'sticky', top: 0, bgcolor: 'background.default', zIndex: 10, py: 1 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {filename && (
            <Typography variant="subtitle1" fontWeight="bold">{filename}</Typography>
          )}
          <Chip label={getLanguageDisplayName(language)} size="small" color={getLanguageColor(language)} />
        </Box>

        <Box display="flex" gap={0.5} alignItems="center">
          <Tooltip title={darkMode ? 'Light theme' : 'Dark theme'}>
            <IconButton size="small" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
            <Button
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopy}
              variant={copied ? 'contained' : 'outlined'}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Tooltip>
          <Tooltip title="Display settings">
            <IconButton
              size="small"
              onClick={() => setShowSettings(s => !s)}
              color={showSettings ? 'primary' : 'default'}
              sx={showSettings ? { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } : {}}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Settings panel */}
      <Collapse in={showSettings}>
        <Paper
          variant="outlined"
          sx={{ mb: 1, p: 1.5, bgcolor: darkMode ? 'grey.700' : 'grey.50', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}
        >
          {/* Word wrap */}
          <Box display="flex" alignItems="center" gap={1}>
            <WrapText fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">Wrap</Typography>
            <Tooltip title={wrapCode ? 'Wrapping on — click to scroll instead' : 'Scrolling — click to wrap long lines'}>
              <IconButton
                size="small"
                onClick={() => setWrapCode(w => !w)}
                color={wrapCode ? 'primary' : 'default'}
                sx={wrapCode ? { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } : {}}
              >
                <WrapText fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Font size */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">Size</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={fontSize}
              onChange={(_, val) => { if (val) setFontSize(val); }}
            >
              {(['S', 'M', 'L'] as const).map(s => (
                <ToggleButton key={s} value={s} sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem' }}>{s}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Line numbers */}
          <Box display="flex" alignItems="center" gap={1}>
            <Tag fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">Line numbers</Typography>
            <Tooltip title={lineNumbers ? 'Hide line numbers' : 'Show line numbers'}>
              <IconButton
                size="small"
                onClick={() => setLineNumbers(n => !n)}
                color={lineNumbers ? 'primary' : 'default'}
                sx={lineNumbers ? { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } : {}}
              >
                <Tag fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Full screen */}
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={fullScreen ? 'Exit full screen' : 'Full screen'}>
              <IconButton
                size="small"
                onClick={() => setFullScreen(f => !f)}
                color={fullScreen ? 'primary' : 'default'}
                sx={fullScreen ? { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } : {}}
              >
                {fullScreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {fullScreen ? 'Exit full screen' : 'Full screen'}
            </Typography>
          </Box>
        </Paper>
      </Collapse>

      {/* Search Bar */}
      <Box mb={1} sx={{ position: 'sticky', top: 48, bgcolor: 'background.default', zIndex: 9, py: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search in code..."
          value={searchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={`${matchCount} ${matchCount === 1 ? 'match' : 'matches'}`}
                    size="small"
                    color={matchCount > 0 ? 'success' : 'default'}
                  />
                  <Tooltip title={caseSensitive ? 'Case sensitive' : 'Case insensitive'}>
                    <Button
                      size="small"
                      onClick={() => setCaseSensitive(!caseSensitive)}
                      variant={caseSensitive ? 'contained' : 'outlined'}
                      sx={{ minWidth: '40px', fontSize: '0.75rem' }}
                    >
                      Aa
                    </Button>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setLocalSearchTerm('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Code Display */}
      <Paper
        ref={codeContainerRef}
        variant="outlined"
        sx={{
          ...(fullScreen ? { flex: 1, overflow: 'auto' } : { overflowX: 'auto' }),
          maxWidth: '100%',
          width: '100%',
          '& pre': {
            margin: 0,
            padding: '0.75rem !important',
            backgroundColor: darkMode ? '#1e1e1e !important' : '#ffffff !important',
            overflowX: 'auto !important'
          },
          '& code': {
            fontSize: `${currentFontSize} !important`,
            lineHeight: '1.5 !important',
            whiteSpace: `${whiteSpaceValue} !important`,
            wordBreak: `${wordBreakValue} !important`,
            overflowWrap: `${wrapCode ? 'break-word' : 'normal'} !important`
          },
          '& .react-syntax-highlighter-line-number.highlighted-line-number': {
            backgroundColor: darkMode ? 'rgba(255, 215, 0, 0.2) !important' : 'rgba(255, 235, 59, 0.2) !important',
            fontWeight: 'bold !important'
          },
          '& .highlighted-line': {
            backgroundColor: darkMode ? 'rgba(255, 215, 0, 0.15) !important' : 'rgba(255, 235, 59, 0.15) !important',
            display: 'block !important'
          }
        }}
      >
        <SyntaxHighlighter
          language={language.toLowerCase()}
          style={darkMode ? vscDarkPlus : vs}
          showLineNumbers={lineNumbers}
          wrapLines={true}
          wrapLongLines={wrapCode}
          lineProps={(lineNumber: number) => {
            const isHighlighted = matchingLines.has(lineNumber);
            return {
              className: isHighlighted ? 'highlighted-line' : '',
              style: isHighlighted ? {
                backgroundColor: darkMode ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 235, 59, 0.15)',
                display: 'block'
              } : {}
            };
          }}
          customStyle={{
            fontSize: currentFontSize,
            borderRadius: 0,
            margin: 0,
            whiteSpace: whiteSpaceValue,
            wordBreak: wordBreakValue,
            lineHeight: 1.5
          }}
        >
          {code}
        </SyntaxHighlighter>
      </Paper>

      {/* Footer */}
      <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {code.split('\n').length} lines • {code.length} characters
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {currentFontSize} • {wrapCode ? 'wrap' : 'scroll'}{lineNumbers ? ' • line numbers' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default CodeViewer;
